import {
  openPersistence,
  type AppSettings,
  type Persistence,
  type PersistenceOptions,
  type StorageFailure,
  type StoredLinkConfig,
  type UnitSystem,
} from '@hgc/core';

/**
 * 应用设置与本地数据。
 *
 * 它把 `@hgc/core` 的 `Persistence` 收窄成设置页要用的那一小块，并守着三条规则：
 *
 * 1. **存储不可用时明说。** 隐私模式 / 站点数据被禁时 `openPersistence` 会降级到内存后端
 *    （`durable === false`），此时"已保存"是假的 —— 刷新就没了。所以每个写操作都会带回
 *    一条"本次不会保存"的提示，而不是静默成功。
 * 2. **清空是不可逆的，先说清会删掉什么。** 这里的每个 clear 都返回"实际删掉了多少"，
 *    界面负责在动手之前用确认弹窗讲清后果。
 * 3. **失败一律带原因与建议动作。** `StorageFailure` 已经准备好中文消息与下一步建议，
 *    这里只做搬运。
 */

export interface StorageNotice {
  readonly level: 'warning' | 'error';
  readonly message: string;
  readonly advice: string | null;
}

export interface LocalDataCounts {
  readonly paramSnapshots: number;
  readonly preflightRecords: number;
  readonly auditEntries: number;
}

export interface ClearResult {
  readonly ok: boolean;
  /** 实际删掉的条数（不是"打算删的"） */
  readonly removed: number;
  readonly notice: StorageNotice | null;
}

function noticeOf(failure: StorageFailure): StorageNotice {
  return { level: 'error', message: failure.message, advice: failure.advice };
}

const NOT_DURABLE: StorageNotice = {
  level: 'warning',
  message: '当前环境无法保存设置（可能处于隐私模式，或本站的站点数据被禁用），本次改动不会被记住。',
  advice: '退出隐私模式、或允许本站保存站点数据后再试；本次的改动在本次会话内仍然有效。',
};

export class AppSettingsStore {
  /** 读取设置失败时的原因（构造完成后由工厂补上）。界面要显示它，而不是静默用默认值 */
  readFailureNotice: StorageNotice | null = null;

  private readonly persistence: Persistence;
  private settings: AppSettings;

  constructor(persistence: Persistence, settings: AppSettings) {
    this.persistence = persistence;
    this.settings = settings;
  }

  get durable(): boolean {
    return this.persistence.durable;
  }

  get current(): AppSettings {
    return this.settings;
  }

  /** 后端类型（`memory` 说明重开页面就没了） */
  get backend(): string {
    return this.persistence.backend;
  }

  /**
   * 存储不可用时的提示。
   *
   * 优先用 `openPersistence` 给出的那条：它带着**具体原因**（"这个环境没有 IndexedDB"
   * 与"打开数据库被占用"是两件事），自己再编一句会把原因抹平。
   */
  get durabilityNotice(): StorageNotice | null {
    if (this.persistence.durable) return null;
    const reported = this.persistence.notices[0];
    return reported === undefined
      ? NOT_DURABLE
      : { level: 'warning', message: reported.message, advice: reported.advice };
  }

  async counts(): Promise<LocalDataCounts> {
    const [snapshots, preflightRecords, auditEntries] = await Promise.all([
      this.persistence.params.list(),
      this.persistence.preflight.count(),
      this.persistence.audit.count(),
    ]);
    return {
      paramSnapshots: snapshots.length,
      preflightRecords,
      auditEntries,
    };
  }

  async setUnits(units: UnitSystem): Promise<StorageNotice | null> {
    const outcome = await this.persistence.settings.save({ units });
    if (!outcome.ok) return noticeOf(outcome.failure);
    this.settings = outcome.value;
    return this.durabilityNotice;
  }

  /** 忘记上次使用的链路（`null` 表示不记住） */
  async forgetLastLink(): Promise<StorageNotice | null> {
    const outcome = await this.persistence.settings.saveLastLink(null);
    if (!outcome.ok) return noticeOf(outcome.failure);
    this.settings = { ...this.settings, lastLink: outcome.value };
    return this.durabilityNotice;
  }

  /** 清空参数快照。逐个槽位删（一个槽位 = 机型 + 固件版本 + last/factory-default） */
  async clearParamSnapshots(): Promise<ClearResult> {
    const list = await this.persistence.params.list();
    let removed = 0;
    for (const summary of list) {
      const outcome = await this.persistence.params.remove(
        summary.model,
        summary.firmware,
        summary.slot,
      );
      if (!outcome.ok) return { ok: false, removed, notice: noticeOf(outcome.failure) };
      removed += outcome.value;
    }
    return { ok: true, removed, notice: this.durabilityNotice };
  }

  /** 清空飞前记录（保留 0 条） */
  async clearPreflightRecords(): Promise<ClearResult> {
    const outcome = await this.persistence.preflight.prune(0);
    if (!outcome.ok) return { ok: false, removed: 0, notice: noticeOf(outcome.failure) };
    return { ok: true, removed: outcome.value, notice: this.durabilityNotice };
  }

  /** 清空审计日志 */
  async clearAuditLog(): Promise<ClearResult> {
    const outcome = await this.persistence.audit.clear();
    if (!outcome.ok) return { ok: false, removed: 0, notice: noticeOf(outcome.failure) };
    return { ok: true, removed: outcome.value, notice: this.durabilityNotice };
  }

  async close(): Promise<void> {
    await this.persistence.close();
  }

  /** 上次使用的链路：**只读展示**（恢复由连接页负责，见 connect/lastLinkStore） */
  get lastLink(): StoredLinkConfig | null {
    return this.settings.lastLink;
  }
}

export async function openAppSettingsStore(
  options: PersistenceOptions = {},
): Promise<AppSettingsStore> {
  const persistence = await openPersistence(options);
  const loaded = await persistence.settings.load();
  const store = new AppSettingsStore(persistence, loaded.settings);
  if (loaded.failure !== null) {
    // 读取失败时仍然给出一个可用的 store（默认值），由界面把原因显示出来
    store.readFailureNotice = noticeOf(loaded.failure);
  }
  return store;
}

/** 用一句话说明"上次连的是什么" */
export function describeStoredLink(config: StoredLinkConfig | null): string {
  if (config === null) return '没有记录';
  switch (config.kind) {
    case 'simulated':
      return `内置模拟机（${config.aircraft === 'plane' ? '固定翼' : '多旋翼'}）`;
    case 'websocket':
      return `WebSocket ${config.url}`;
    case 'serial':
      return `串口 @ ${config.baudRate ?? '默认波特率'}`;
    case 'bluetooth':
      return '蓝牙（Nordic UART 透传）';
  }
}
